import React from "react";
import { Mail, Linkedin, Github, Phone, MapPin, Download } from "lucide-react";
import { motion } from "motion/react";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import { useLocalizedContent } from "../context/LanguageContext";
import { useParallax } from "../hooks/useParallax";
import AnimatedText from "./AnimatedText";
import ParallaxScene from "./ParallaxScene";

const Hero = () => {
  const [portraitLoaded, setPortraitLoaded] = React.useState(false);
  const [portraitError, setPortraitError] = React.useState(false);
  const { personalInfo, hero } = useLocalizedContent();
  const contentParallax = useParallax(0.02);
  const glowParallax = useParallax(0.08);
  const portraitParallax = useParallax(-0.04);

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.12, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 18 },
    show: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 140, damping: 18 },
    },
  };

  const floatMotion = { y: [0, -8, 0] };
  const floatTransition = (index) => ({
    duration: 4.5,
    repeat: Infinity,
    ease: "easeInOut",
    delay: index * 0.2,
  });

  const handleDownloadCV = () => {
    window.open(personalInfo.cvUrl, "_blank");
  };

  return (
    <motion.section
      id="home"
      className="section-fade relative min-h-screen flex items-center justify-center overflow-hidden"
      initial="hidden"
      animate="show"
    >
      {/* Cinematic Background with Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-zinc-900 to-black">
        <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/10 via-transparent to-cyan-600/10"></div>
        <ParallaxScene />
      </div>

      {/* Content Grid */}
      <div className="relative z-10 container mx-auto px-6 lg:px-12 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div
            className="transition-transform duration-500"
            style={{ transform: `translate3d(0, ${contentParallax}px, 0)` }}
          >
            <motion.div className="space-y-8" variants={containerVariants}>
            {/* Open to Work Badge */}
              <motion.div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 backdrop-blur-md border border-white/10"
                variants={itemVariants}
              >
                <motion.div
                  className="w-2 h-2 bg-green-400 rounded-full"
                  animate={{ scale: [1, 1.4, 1], opacity: [0.7, 1, 0.7] }}
                  transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                ></motion.div>
                <span className="text-sm text-white/80">
                  {hero.badgeText}
                </span>
              </motion.div>

            {/* Role Tag */}
              <motion.div variants={itemVariants}>
                <span className="inline-block px-4 py-1.5 rounded-full text-xs font-medium tracking-wider uppercase bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                  {personalInfo.title.split("&")[0].trim()}
                </span>
              </motion.div>

            {/* Name */}
              <AnimatedText
                text={personalInfo.name}
                as="h1"
                className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight"
                stagger={0.06}
                delay={0.05}
              />

            {/* Title */}
              <AnimatedText
                text={personalInfo.title}
                as="p"
                className="text-xl md:text-2xl text-white/60 font-light"
                stagger={0.05}
                delay={0.1}
              />

            {/* Bio */}
              <AnimatedText
                text={personalInfo.bio}
                as="p"
                className="text-lg text-white/50 max-w-xl leading-relaxed"
                stagger={0.02}
                delay={0.15}
              />

            {/* Contact Icons */}
              <motion.div className="flex flex-wrap gap-3" variants={itemVariants}>
                {[
                  {
                    href: `mailto:${personalInfo.email}`,
                    icon: Mail,
                  },
                  {
                    href: `https://${personalInfo.linkedin}`,
                    icon: Linkedin,
                    external: true,
                  },
                  {
                    href: `https://${personalInfo.github}`,
                    icon: Github,
                    external: true,
                  },
                  {
                    href: `tel:${personalInfo.phone}`,
                    icon: Phone,
                  },
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <motion.a
                      key={item.href}
                      href={item.href}
                      target={item.external ? "_blank" : undefined}
                      rel={item.external ? "noopener noreferrer" : undefined}
                      className="group"
                      variants={itemVariants}
                      whileHover={{ y: -4, scale: 1.06 }}
                      whileTap={{ scale: 0.98 }}
                      transition={{ type: "spring", stiffness: 220, damping: 16 }}
                    >
                      <motion.div
                        animate={floatMotion}
                        transition={floatTransition(index)}
                      >
                        <div className="p-3 rounded-xl bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 hover:border-blue-500/50 transition-all duration-300">
                          <Icon className="w-5 h-5 text-white/70 group-hover:text-blue-300 transition-colors" />
                        </div>
                      </motion.div>
                    </motion.a>
                  );
                })}
                <motion.div
                  className="group cursor-pointer"
                  variants={itemVariants}
                  whileHover={{ y: -4, scale: 1.06 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: "spring", stiffness: 220, damping: 16 }}
                >
                  <motion.div animate={floatMotion} transition={floatTransition(4)}>
                    <div className="p-3 rounded-xl bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 hover:border-blue-500/50 transition-all duration-300">
                      <MapPin className="w-5 h-5 text-white/70 group-hover:text-blue-300 transition-colors" />
                    </div>
                  </motion.div>
                </motion.div>
              </motion.div>

            {/* CTA Button */}
              <motion.div variants={itemVariants} className="inline-block">
                <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                  <Button
                    onClick={handleDownloadCV}
                    className="group px-8 py-6 rounded-full bg-gradient-to-r from-blue-400 to-cyan-500 hover:from-blue-500 hover:to-cyan-600 text-white font-semibold text-base shadow-lg shadow-blue-500/40 hover:shadow-blue-500/70 transition-all duration-300 flex items-center gap-2"
                  >
                    <Download className="w-5 h-5 group-hover:animate-bounce" />
                    {hero.downloadCta}
                  </Button>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>

          {/* Right Content - Portrait */}
          <div className="relative lg:block hidden">
            {/* Glow Effects */}
            <div
              className="absolute -top-20 -right-20 w-96 h-96"
              style={{ transform: `translate3d(0, ${glowParallax * 1.5}px, 0)` }}
            >
              <motion.div
                className="w-full h-full bg-blue-500/30 rounded-full blur-3xl"
                animate={{ scale: [1, 1.08, 1], opacity: [0.4, 0.7, 0.4] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              ></motion.div>
            </div>
            <div
              className="absolute -bottom-20 -left-20 w-96 h-96"
              style={{ transform: `translate3d(0, ${-glowParallax}px, 0)` }}
            >
              <motion.div
                className="w-full h-full bg-cyan-500/20 rounded-full blur-3xl"
                animate={{ scale: [1, 1.12, 1], opacity: [0.35, 0.6, 0.35] }}
                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
              ></motion.div>
            </div>

            {/* Portrait Container */}
            <div
              className="relative z-10"
              style={{ transform: `translate3d(0, ${portraitParallax}px, 0)` }}
            >
              <motion.div
                className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl"
                variants={itemVariants}
              >
                {/* Rim Light Overlay */}
                <div className="absolute inset-0 bg-gradient-to-l from-blue-500/40 via-transparent to-transparent z-10"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10"></div>

                {/* Image */}
                <img
                  src={personalInfo.portrait}
                  alt={personalInfo.name}
                  loading="lazy"
                  decoding="async"
                  onLoad={() => setPortraitLoaded(true)}
                  onError={() => setPortraitError(true)}
                  className={`w-full h-[600px] object-cover transition-opacity duration-500 ${
                    portraitLoaded && !portraitError ? "opacity-100" : "opacity-0"
                  }`}
                />

                {!portraitLoaded && !portraitError && (
                  <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-black/40 backdrop-blur">
                    <Skeleton className="w-20 h-20 rounded-full bg-white/10" />
                    <span className="text-xs uppercase tracking-wide text-white/70">
                      {hero.loadingPortrait}
                    </span>
                  </div>
                )}

                {portraitError && (
                  <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-black/60 text-white/70">
                    <span className="text-sm font-semibold">
                      {hero.portraitUnavailableTitle}
                    </span>
                    <span className="text-xs">
                      {hero.portraitUnavailableSubtitle}
                    </span>
                  </div>
                )}

                {/* Glass Badge */}
                <div className="absolute bottom-6 left-6 right-6 z-20">
                  <motion.div
                    className="p-4 rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20"
                    whileHover={{ scale: 1.02 }}
                  >
                    <p className="text-white font-medium">
                      {hero.locationPrefix} {personalInfo.location}
                    </p>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <motion.div
          className="flex flex-col items-center gap-2"
          animate={{ y: [0, 12, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        >
          <span className="text-white/40 text-xs uppercase tracking-wider">
            {hero.scrollText}
          </span>
          <div className="w-px h-12 bg-gradient-to-b from-white/40 to-transparent"></div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default Hero;
